import React from 'react';
import PropTypes from 'prop-types';
import {Trans} from 'react-i18next';
import forwardRefWithTranslation from '@/common/forwardRefWithTranslation';

import CustomHtmlSlot from '@/common/components/CustomHtmlSlot';
import LoyaltyOptInCheckbox from "@/common/customer.loyalty/loyalty-opt-in-checkbox";
import DesktopView from "@/common/customer.loyalty/loyalty-opt-in-checkbox/DesktopView";

import OfflineMethod from './offline-method';
import TokenMethod from './token-method';
import OnlineMethod from './online-method';
import IframeForm from './iframe-form';
import _ from 'lodash';
import BalancesSpend from "@/common/customer-balances/BalancesSpend";
import OrderServiceFee from "@/desktop/order.confirmation/order-confirmation-payment/order-service-fee";

const {
    Order,
    Customer,
    headoffice,
} = Mobi2Go;

const headoffice_loyalty_opt_in_feature_flag =
    Mobi2Go.app.feature_flags?.headoffice_loyalty_opt_in;

const headoffice_loyalty_opt_in_enabled = headoffice.loyalty_opt_in_enabled;

class OrderConfirmationPayment extends React.Component {
    static propTypes = {
        progress_phase: PropTypes.string.isRequired,
        payment_method: PropTypes.string,
        payment_total: PropTypes.object,
        total: PropTypes.object,
        currency_abrv: PropTypes.string,
        method: PropTypes.string,
        onCloseModal: PropTypes.func.isRequired,
        show_payment_processing_warning: PropTypes.bool.isRequired,
        location: PropTypes.object,
        confirmClickHandler: PropTypes.func.isRequired,
        credit: PropTypes.object,
        service_fee: PropTypes.object.isRequired,
        t: PropTypes.func.isRequired,
    };

    constructor() {
        super();

        this.state = {
            payment_options: [],
            available_methods: [],
            available_payment_processors: [],
            card_tokens: [],
            selected_card_token: {},
            is_password_provided: false,
            payment_processor: null,
            payment_processor_iframe_url: null,
            credit_applied: false,
            loyalty_opt_in_enabled: false,
            rebates: [],
        };
        this.has_selection_been_made = false;
        this.current_token = null;
    }

    getOrderEventBindings = () => {
        return {
            updateAvailablePaymentOptions: this.onUpdateAvailablePaymentOptions,
            updateAvailablePaymentMethods: this.onUpdateAvailablePaymentMethods,
            updateAvailablePaymentProcessors: this
                .onUpdateAvailablePaymentProcessors,
            updateAvailablePaymentCardTokens: this
                .onUpdateAvailablePaymentCardTokens,
            updatePaymentCardToken: this.onUpdatePaymentCardToken,
            passwordProvided: this.onPasswordProvided,
            updatePaymentProcessor: this.onUpdatePaymentProcessor,
            updatePaymentMethod: this.onUpdatePaymentMethod,
            reset: this.onReset,
            updatePaymentProcessorIframeUrl: this
                .onUpdatePaymentProcessorIframeUrl,
            updateLocation: this.onUpdateLocation,
            updatedRebates: this.onUpdatedRebates,
        };
    };

    componentDidMount = () => {
        Order.bind(this.getOrderEventBindings());
        const payment_processor = Order.getPaymentProcessor();
        this.setState(
            {
                payment_options: Order.getAvailablePaymentOptions(),
                available_methods: Order.getAvailablePaymentMethods(),
                available_payment_processors: Order.getAvailablePaymentProcessorInstances(),
                card_tokens: Order.getAvailablePaymentCardTokens(),
                selected_card_token: Order.getPaymentCardToken(),
                is_password_provided: Order.isPasswordProvided(),
                payment_processor,
                payment_processor_iframe_url:
                    payment_processor && payment_processor.getIframeUrl
                        ? payment_processor.getIframeUrl()
                        : null,
                loyalty_opt_in_enabled: Order.getLocation()
                    ? Order.getLocation().isLoyaltyOptInEnabled()
                    : headoffice_loyalty_opt_in_enabled,
                rebates: Order.getRebates(),
                service_fee: Order.getServiceFee(),
            },
            () => {
                this.has_selection_been_made = false;
                this.selectDefault();
            }
        );
        Order.unapplyCredit();
    };

    componentWillUnmount = () => {
        Order.unbind(this.getOrderEventBindings());
    };

    showErrorMessage = (title, message) => {
        Mobi2Go.UI.centeredAlert({
            title: title,
            body: message,
            temp: true,
            temp_duration: 10000,
        });
    };

    onUpdateLocation = () => {
        this.setState({
            loyalty_opt_in_enabled: Order.getLocation()
                ? Order.getLocation().isLoyaltyOptInEnabled()
                : headoffice_loyalty_opt_in_enabled,
        });
    };

    onUpdatedRebates = (rebates) => {
        this.setState({
            rebates: rebates,
        });
    };

    selectDefault = () => {
        if (this.props.progress_phase === 'confirmed') {
            return;
        }
        const options = this.state.payment_options;
        if (!this.has_selection_been_made && options && options.length) {
            Order.setPaymentOption(options[0]);
        }
    };

    getTokenPassword = () => {
        if (this.current_token === null) {
            return '';
        }
        return this.current_token.getPassword();
    };

    focusTokenPassword = () => {
        if (this.current_token) {
            this.current_token.focusPassword();
        }
    };

    onUpdatePaymentProcessorIframeUrl = () => {
        this.setState({
            payment_processor_iframe_url: Order.getPaymentProcessor().getIframeUrl(),
        });
    };

    onUpdatePaymentMethod = () => {
        this.selectDefault();
    };

    onReset = () => {
        this.has_selection_been_made = false;
    };

    onUpdatePaymentProcessor = () => {
        this.setState({
            payment_processor: Order.getPaymentProcessor(),
        });
    };

    onPasswordProvided = () => {
        this.setState({
            is_password_provided: Order.isPasswordProvided(),
        });
    };

    onUpdatePaymentCardToken = () => {
        this.setState({
            selected_card_token: Order.getPaymentCardToken(),
        });
    };

    onUpdateAvailablePaymentCardTokens = () => {
        this.setState({
            card_tokens: Order.getAvailablePaymentCardTokens(),
        });
    };

    onUpdateAvailablePaymentProcessors = () => {
        this.setState({
            available_payment_processors: Order.getAvailablePaymentProcessorInstances(),
        });
    };

    onUpdateAvailablePaymentMethods = () => {
        this.setState({
            available_methods: Order.getAvailablePaymentMethods(),
        });
    };

    onUpdateAvailablePaymentOptions = () => {
        this.setState(
            {
                payment_options: Order.getAvailablePaymentOptions(),
            },
            () => {
                this.selectDefault();
            }
        );
    };

    setPaymentMethod = (method, processor, card_token) => {
        this.has_selection_been_made = true;
        if (card_token) {
            Order.setPaymentCardTokenById(card_token.id);
        } else {
            Order.setPaymentCardToken(null);
        }
        if (processor) {
            Order.setPaymentProcessor({ name: processor });
        }
        Order.setPaymentMethod(method);
    };

    onCreditClick = () => {
        if (!this.state.credit_applied) {
            Order.applyAvailableCredit();
        } else {
            Order.unapplyCredit();
        }
        this.setState({ credit_applied: !this.state.credit_applied });
    };

    loyalty_opt_in_display_credit = () => {
        if (!headoffice_loyalty_opt_in_feature_flag) {
            // If the feature flag is disabled, these checks should not affect credit being shown
            return true;
        }

        if (!this.state.loyalty_opt_in_enabled) {
            return false;
        }

        return Customer.getLoyaltyOptIn();
    };

    render() {
        const { t } = this.props;
        const offline_only =
            this.state.available_methods.length === 1 &&
            this.state.available_methods[0] === 'offline';
        const online_only =
            this.state.available_methods.length === 1 &&
            this.state.available_methods[0] === 'online';
        const show_credit =
            (Mobi2Go.headoffice.credit_program.enabled ||
                // Loyalty scheme that uses credit
                _.reduce(
                    Customer.getLoyalty(),
                    function(result, scheme) {
                        return result || scheme.features?.uses_credit;
                    },
                    false
                )) &&
            this.props.payment_method === 'online' &&
            this.props.customer_credit_balance &&
            this.props.customer_credit_balance.toFloat() > 0 &&
            this.loyalty_opt_in_display_credit &&
            !(
                this.props.payment_total.toFloat() <= 0 &&
                this.props.credit.toFloat() <= 0
            );
        const credit_minimum_spend = Mobi2Go._class.Common.Price(
            Mobi2Go.headoffice.credit_program.minimum_spend
        );
        const is_credit_spendable =
            this.props.total.toFloat() >= credit_minimum_spend.toFloat() &&
            this.props.customer_credit_balance &&
            this.props.customer_credit_balance.toFloat() >=
                credit_minimum_spend.toFloat();
        const show_payment_total =
            this.props.payment_total.toFloat() > 0 &&
            this.props.total.toFloat() !== this.props.payment_total.toFloat();
        const show_service_fee =
            show_payment_total &&
            this.props.service_fee.total.toFloat() > 0 &&
            this.props.service_fee.total_add.toFloat() <= 0;

        const show_payment_methods = this.props.payment_total.toFloat() > 0;
        this.current_token = null;
        const loyalty_opt_in_enabled = this.state.loyalty_opt_in_enabled;

        if (offline_only || this.props.total.toFloat() === 0.0) {
            return null;
        }

        return (
            <div>
                <div className="m2g-well m2g-order-confirmation-payment">
                    <CustomHtmlSlot slot="desktop-order-confirmation-modal-payment-above" />
                    <div
                        className="m2g-order-confirmation-payment-methods"
                        style={{
                            display:
                                this.props.progress_phase === 'unconfirmed'
                                    ? 'inline-block'
                                    : 'none',
                        }}
                    >
                        <div className="m2g-order-confirmation-payment-methods-title">
                            {t('payment')}
                        </div>

                        <BalancesSpend rebates={this.state.rebates} />

                        {show_credit && (
                            <div
                                onClick={this.onCreditClick}
                                className="m2g-order-confirmation-payment-method m2g-order-confirmation-payment-credit"
                                data-checked={this.state.credit_applied}
                            >
                                {is_credit_spendable ? (
                                    [
                                        <div
                                            key="icon--tick"
                                            className="m2g-order-confirmation-payment-credit-checkbox m2g-icon m2g-icon--checkbox m2g-icon--tick"
                                        />,
                                        <label
                                            key="credit-label"
                                            className="m2g-order-confirmation-payment-credit-label"
                                        >
                                            {t('common:spend_credit_message')}
                                        </label>,
                                        <div
                                            key="credit-value"
                                            className="m2g-order-confirmation-payment-credit-value"
                                        >
                                            {this.props.credit.toFloat() >
                                            0.0 ? (
                                                t('spending_credit', {
                                                    credit: t('common:price', {
                                                        price: this.props
                                                            .credit,
                                                    }),
                                                })
                                            ) : (
                                                <span className="m2g-text--small m2g-text--offset">
                                                    {t('available_credit', {
                                                        available_credit: t(
                                                            'common:price',
                                                            {
                                                                price: this
                                                                    .props
                                                                    .customer_credit_balance,
                                                            }
                                                        ),
                                                    })}
                                                </span>
                                            )}
                                        </div>,
                                    ]
                                ) : (
                                    <label className="m2g-text--offset m2g-text--italic m2g-text--small">
                                        {t('credit_minimum_spend', {
                                            credit_program_title: t(
                                                'common:credit_program_title'
                                            ),
                                            credit_minimum_spend: t(
                                                'common:price',
                                                { price: credit_minimum_spend }
                                            ),
                                        })}
                                    </label>
                                )}
                            </div>
                        )}

                        {loyalty_opt_in_enabled && (
                            <LoyaltyOptInCheckbox
                                ViewComponent={DesktopView}
                                optInString={t(
                                    'loyalty_call_to_action_message'
                                )}
                                CustomHtmlSlot={CustomHtmlSlot}
                            />
                        )}

                        {show_service_fee && (
                            <OrderServiceFee
                                service_fee={this.props.service_fee.total}
                            />
                        )}

                        {show_payment_total && (
                            <div className="m2g-order-confirmation-payment-total">
                                <Trans
                                    i18nKey="order.confirmation:total_to_pay"
                                    values={{
                                        currency_abrv: this.props.currency_abrv,
                                        payment_total: t('common:price', {
                                            price: this.props.payment_total,
                                        }),
                                    }}
                                >
                                    <div className="m2g-order-confirmation-payment-total-label">
                                        Total to pay ({'{{currency_abrv}}'})
                                    </div>
                                    <div className="m2g-order-confirmation-payment-total-value">
                                        {'{{total}}'}
                                    </div>
                                </Trans>
                            </div>
                        )}

                        {show_payment_methods &&
                            this.state.payment_options.map(payment_option => {
                                if (payment_option.method === 'online') {
                                    if (payment_option.token) {
                                        const is_password_required =
                                            !this.state.is_password_provided &&
                                            Order.isPasswordRequiredForPaymentToken(
                                                payment_option.token
                                            );
                                        return (
                                            <TokenMethod
                                                key={payment_option.token.id}
                                                token={payment_option.token}
                                                card_tokens={
                                                    this.state.card_tokens
                                                }
                                                payment_method={
                                                    this.props.payment_method
                                                }
                                                selected_card_token={
                                                    this.state
                                                        .selected_card_token
                                                }
                                                is_password_required={
                                                    is_password_required
                                                }
                                                onClick={() =>
                                                    this.setPaymentMethod(
                                                        'online',
                                                        payment_option.processor,
                                                        payment_option.token
                                                    )
                                                }
                                                onCloseModal={
                                                    this.props.onCloseModal
                                                }
                                                confirmClickHandler={
                                                    this.props
                                                        .confirmClickHandler
                                                }
                                                ref={token_method => {
                                                    if (
                                                        token_method &&
                                                        token_method.isMethodActive()
                                                    ) {
                                                        this.current_token = token_method;
                                                    }
                                                }}
                                            />
                                        );
                                    }
                                    return (
                                        <OnlineMethod
                                            key={payment_option.processor}
                                            processor={payment_option.processor}
                                            available_payment_processors={
                                                this.state
                                                    .available_payment_processors
                                            }
                                            card_tokens={this.state.card_tokens}
                                            payment_method={
                                                this.props.payment_method
                                            }
                                            payment_card_token={
                                                this.state.selected_card_token
                                            }
                                            payment_processor={
                                                this.state.payment_processor
                                            }
                                            is_offline_available={!online_only}
                                            location={this.props.location}
                                            onClick={() =>
                                                this.setPaymentMethod(
                                                    'online',
                                                    payment_option.processor
                                                )
                                            }
                                        />
                                    );
                                }
                                if (payment_option.method === 'offline') {
                                    return (
                                        <OfflineMethod
                                            key="offline"
                                            payment_method={
                                                this.props.payment_method
                                            }
                                            method={this.props.method}
                                            onClick={() =>
                                                this.setPaymentMethod('offline')
                                            }
                                        />
                                    );
                                }
                                return null;
                            })}
                    </div>
                    {this.props.progress_phase === 'confirming' &&
                        this.props.payment_method === 'online' &&
                        this.state.payment_processor &&
                        this.state.payment_processor.getFormType() ===
                            'iframe' &&
                        this.props.show_payment_processing_warning === true && (
                            <div
                                className="m2g-order-confirmation-payment-processing-warning m2g-alert m2g-alert--warning"
                                data-payment-processing-warning
                            >
                                <span className="m2g-alert-icon m2g-alert-icon--large m2g-icon m2g-icon--alert m2g-order-confirmation-payment-processing-warning-icon" />
                                <div className="m2g-alert-content m2g-order-confirmation-payment-processing-warning-content">
                                    <Trans i18nKey="order.confirmation:payment_warning">
                                        <strong>Please note:</strong>&nbsp;
                                        Payment may take up to&nbsp;
                                        <strong>5 minutes</strong>&nbsp;to
                                        process.&nbsp; Do not navigate away from
                                        this page or close your browser while
                                        completing your payment.
                                    </Trans>
                                </div>
                            </div>
                        )}
                    {this.props.progress_phase === 'confirming' &&
                        this.props.payment_method === 'online' &&
                        this.state.payment_processor &&
                        this.state.payment_processor.getFormType() ===
                            'iframe' && (
                            <IframeForm
                                payment_processor={this.state.payment_processor}
                                payment_processor_iframe_url={
                                    this.state.payment_processor_iframe_url
                                }
                            />
                        )}
                    {this.props.progress_phase === 'confirmed' &&
                        this.props.payment_method === 'online' && (
                            <div className="m2g-order-confirmation-payment-success">
                                <span className="m2g-order-confirmation-payment-success-text">
                                    {t('paid')}
                                </span>
                            </div>
                        )}
                    <CustomHtmlSlot slot="desktop-order-confirmation-modal-payment-below" />
                </div>
            </div>
        );
    }
}

export default forwardRefWithTranslation('order.confirmation')(
    OrderConfirmationPayment
);
